/**
 * User Login API Service
 * 
 * Handles authentication, user management, and session handling.
 * Uses cookie-based session management with credentials: 'include'.
 * 
 * @module userLoginApi
 */

import { apiConfig } from '@/config/apiConfig';
import type {
  LoginRequest,
  LoginResponse,
  MfaLoginVerifyRequest,
  MfaLoginVerifyResponse,
  MfaStatusResponse,
  MfaEnrollResponse,
  MfaToggleResponse,
  LogoutResponse,
  ValidateSessionResponse,
  RegistrationRequest,
  RegistrationResponse,
  ActivateResponse,
  UserDetails,
  UpdateUserDetailsRequest,
  UpdateUserDetailsResponse,
  StorageSpaceResponse,
  UpdatePasswordRequest,
  UpdatePasswordResponse,
  ForgotPasswordResponse,
  ValidateTokenResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  GetAddressListResponse,
  AddAddressRequest,
  AddAddressResponse,
  EditAddressRequest,
  EditAddressResponse,
  DeleteAddressResponse,
  ListUsersRequest,
  ListUsersResponse,
  ImpersonationSessionResponse,
  AdminCheckResponse,
  AdminListResponse,
  AdminUserItem,
  AddAdminRequest,
  AddAdminResponse,
  RemoveAdminRequest,
  RemoveAdminResponse,
  StartImpersonationResponse,
  StopImpersonationResponse,
  ActivateUserRequest,
  ActivateUserResponse,
  DeactivateUserRequest,
  DeactivateUserResponse,
  IsUserAnonymousResponse,
  GetCartResponse,
  UpdateJourneyRequest,
  UpdateJourneyResponse,
  OnboardingPreference,
  UserLoginAnalyticsResponse,
  AdminReviewUserResponse,
  AdminReviewActionResponse,
} from '@/types/user-login';

const BASE_URL = apiConfig.baseUrls.userLogin;
const endpoints = apiConfig.userLoginEndpoints;

/**
 * Custom error class for User Login API errors
 */
export class UserLoginApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public detail?: string
  ) {
    super(message);
    this.name = 'UserLoginApiError';
  }
}

/**
 * Base fetch function with credentials and error handling
 */
async function fetchWithCredentials<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (response.status === 401) {
    // Session expired - could trigger redirect
    throw new UserLoginApiError('Session expired', 401, 'Unauthorized');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new UserLoginApiError(
      error.detail || 'Request failed',
      response.status,
      error.detail
    );
  }

  return response.json();
}

// ============================================
// Authentication
// ============================================

/**
 * Login with email and password
 */
export async function login(
  username: string,
  password: string,
  tempUserID?: number | null
): Promise<LoginResponse> {
  const body: LoginRequest = {
    username,
    password,
    temp_userID: tempUserID ?? null,
  };
  
  return fetchWithCredentials<LoginResponse>(endpoints.login, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Redeem a pending MFA login (see LoginResponse.mfa_required) with the account's current
 * TOTP code. On success this establishes the real session, same as a normal login.
 */
export async function loginVerifyMfa(
  pendingToken: string,
  code: string
): Promise<MfaLoginVerifyResponse> {
  const body: MfaLoginVerifyRequest = { pending_token: pendingToken, code };

  return fetchWithCredentials<MfaLoginVerifyResponse>(endpoints.mfaLoginVerify, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function getMfaStatus(): Promise<MfaStatusResponse> {
  return fetchWithCredentials<MfaStatusResponse>(endpoints.mfaStatus, { method: 'GET' });
}

/** Generates a fresh, unconfirmed secret. MFA is not enforced until verifyMfaEnrollment succeeds. */
export async function enrollMfa(): Promise<MfaEnrollResponse> {
  return fetchWithCredentials<MfaEnrollResponse>(endpoints.mfaEnroll, { method: 'POST' });
}

export async function verifyMfaEnrollment(code: string): Promise<MfaToggleResponse> {
  return fetchWithCredentials<MfaToggleResponse>(endpoints.mfaVerify, {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

/** Requires a current authenticator code, not just the session. */
export async function disableMfa(code: string): Promise<MfaToggleResponse> {
  return fetchWithCredentials<MfaToggleResponse>(endpoints.mfaDisable, {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

/**
 * Logout current user
 */
export async function logout(): Promise<LogoutResponse> {
  return fetchWithCredentials<LogoutResponse>(endpoints.logout, {
    method: 'POST',
  });
}

/**
 * Validate current session
 */
export async function validateSession(): Promise<ValidateSessionResponse> {
  return fetchWithCredentials<ValidateSessionResponse>(endpoints.validateSession, {
    method: 'POST',
  });
}

// ============================================
// Registration
// ============================================

/**
 * Register a new user
 */
export async function register(data: RegistrationRequest): Promise<RegistrationResponse> {
  return fetchWithCredentials<RegistrationResponse>(endpoints.register, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Activate user account with token
 */
export async function activateAccount(token: string): Promise<ActivateResponse> {
  return fetchWithCredentials<ActivateResponse>(endpoints.activate, {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

/**
 * Resend activation link
 */
export async function resendActivationLink(): Promise<{ status: string; message: string }> {
  return fetchWithCredentials(endpoints.resendActivation, {
    method: 'POST',
  });
}

// ============================================
// User Profile
// ============================================

/**
 * Get current user details.
 * Uses cookie-based auth (credentials: 'include').
 * - On *.boostt.ai: backend should set cookie with Domain=.boostt.ai, SameSite=Lax.
 * - On localhost → staging API: backend must set cookie with SameSite=None; Secure and
 *   allow Origin http://localhost:* in CORS with credentials, else this returns 401 after login.
 *   See docs/USER_LOGIN_COOKIE_REQUIREMENTS.md.
 */
export async function getUserDetails(): Promise<UserDetails> {
  return fetchWithCredentials<UserDetails>(endpoints.getUserDetails, {
    method: 'POST',
  });
}

/**
 * Update user details
 */
export async function updateUserDetails(
  data: UpdateUserDetailsRequest
): Promise<UpdateUserDetailsResponse> {
  return fetchWithCredentials<UpdateUserDetailsResponse>(endpoints.updateUserDetails, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get user storage space info
 */
export async function getUserStorageSpace(): Promise<StorageSpaceResponse> {
  return fetchWithCredentials<StorageSpaceResponse>(endpoints.getStorageSpace, {
    method: 'POST',
  });
}

/**
 * Update user journey/onboarding progress
 */
export async function updateJourney(
  data: UpdateJourneyRequest
): Promise<UpdateJourneyResponse> {
  return fetchWithCredentials<UpdateJourneyResponse>(endpoints.updateJourney, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Read the post-login platform onboarding preference (single endpoint: GET reads, PUT saves)
 */
export async function getOnboardingPreference(): Promise<OnboardingPreference> {
  return fetchWithCredentials<OnboardingPreference>(endpoints.onboardingPreference, {
    method: 'GET',
  });
}

/**
 * Save the post-login platform onboarding "don't show again" preference
 */
export async function updateOnboardingPreference(
  data: OnboardingPreference
): Promise<OnboardingPreference> {
  return fetchWithCredentials<OnboardingPreference>(endpoints.onboardingPreference, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// ============================================
// Password Management
// ============================================

/**
 * Update password (requires current password)
 */
export async function updatePassword(
  oldPassword: string,
  newPassword: string
): Promise<UpdatePasswordResponse> {
  const body: UpdatePasswordRequest = {
    oldpassword: oldPassword,
    password: newPassword,
  };
  
  return fetchWithCredentials<UpdatePasswordResponse>(endpoints.updatePassword, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Request password reset email
 */
export async function forgotPassword(email: string): Promise<ForgotPasswordResponse> {
  return fetchWithCredentials<ForgotPasswordResponse>(endpoints.forgotPassword, {
    method: 'POST',
    body: JSON.stringify({ useremail: email }),
  });
}

/**
 * Validate password reset token
 */
export async function validateResetToken(token: string): Promise<ValidateTokenResponse> {
  return fetchWithCredentials<ValidateTokenResponse>(endpoints.validateToken, {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

/**
 * Reset password with token
 */
export async function resetPassword(
  resetToken: string,
  password: string,
  confirmPassword: string
): Promise<ResetPasswordResponse> {
  const body: ResetPasswordRequest = {
    resettoken: resetToken,
    password,
    retypePassword: confirmPassword,
  };
  
  return fetchWithCredentials<ResetPasswordResponse>(endpoints.resetPassword, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ============================================
// Address Management
// ============================================

/**
 * Get all user addresses
 */
export async function getAddresses(): Promise<GetAddressListResponse> {
  return fetchWithCredentials<GetAddressListResponse>(endpoints.getAddresses, {
    method: 'POST',
  });
}

/**
 * Add a new address
 */
export async function addAddress(data: AddAddressRequest): Promise<AddAddressResponse> {
  return fetchWithCredentials<AddAddressResponse>(endpoints.addAddress, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Edit an existing address
 */
export async function editAddress(data: EditAddressRequest): Promise<EditAddressResponse> {
  return fetchWithCredentials<EditAddressResponse>(endpoints.editAddress, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Delete an address
 */
export async function deleteAddress(addressId: string): Promise<DeleteAddressResponse> {
  return fetchWithCredentials<DeleteAddressResponse>(endpoints.deleteAddress, {
    method: 'POST',
    body: JSON.stringify({ addressID: addressId }),
  });
}

// ============================================
// Administration (Admin Only)
// ============================================

export async function checkAdmin(): Promise<AdminCheckResponse> {
  return fetchWithCredentials<AdminCheckResponse>(endpoints.adminCheck, {
    method: 'GET',
  });
}

/**
 * List platform admins.
 */
export async function listAdmins(): Promise<AdminListResponse> {
  return fetchWithCredentials<AdminListResponse>(endpoints.adminList, {
    method: 'GET',
  });
}

/**
 * Grant admin privileges to a user.
 */
export async function addAdmin(userId: number): Promise<AddAdminResponse> {
  const body: AddAdminRequest = { user_id: userId };
  return fetchWithCredentials<AddAdminResponse>(endpoints.adminAdd, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Remove admin privileges from a user.
 */
export async function removeAdmin(userId: number): Promise<RemoveAdminResponse> {
  const body: RemoveAdminRequest = { user_id: userId };
  return fetchWithCredentials<RemoveAdminResponse>(endpoints.adminRemove, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * List all activated users (admin only)
 */
export async function listUsers(limit?: number): Promise<ListUsersResponse> {
  const body: ListUsersRequest = { limit };
  
  return fetchWithCredentials<ListUsersResponse>(endpoints.listUsers, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * No-session admin review flow — reached via the token in an admin-notification
 * email link (see user-login's app/services/admin_review_token.py). Does not
 * use fetchWithCredentials's 401-as-"session expired" handling since these
 * endpoints don't check a session at all; an invalid/expired token comes back
 * as a plain 400 the caller already handles via the thrown UserLoginApiError.
 */
export async function getReviewUser(token: string): Promise<AdminReviewUserResponse> {
  return fetchWithCredentials<AdminReviewUserResponse>(
    `${endpoints.reviewUser}?token=${encodeURIComponent(token)}`,
    { method: 'GET' }
  );
}

export async function reviewUserAction(
  token: string,
  action: 'activate' | 'deactivate',
  approveWithoutDemo?: boolean
): Promise<AdminReviewActionResponse> {
  return fetchWithCredentials<AdminReviewActionResponse>(endpoints.reviewUserAction, {
    method: 'POST',
    body: JSON.stringify({ token, action, approveWithoutDemo: approveWithoutDemo ?? false }),
  });
}

export async function getImpersonationSession(
  options: { signal?: AbortSignal } = {}
): Promise<ImpersonationSessionResponse> {
  return fetchWithCredentials<ImpersonationSessionResponse>(endpoints.impersonationSession, {
    method: 'POST',
    signal: options.signal,
  });
}

export async function listImpersonationUsers(params: {
  page?: number;
  limit?: number;
  search?: string;
} = {}): Promise<ListUsersResponse> {
  return fetchWithCredentials<ListUsersResponse>(endpoints.impersonationListUsers, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function startImpersonation(targetUserId: number): Promise<StartImpersonationResponse> {
  return fetchWithCredentials<StartImpersonationResponse>(endpoints.impersonationStart, {
    method: 'POST',
    body: JSON.stringify({ target_user_id: targetUserId }),
  });
}

export async function stopImpersonation(): Promise<StopImpersonationResponse> {
  return fetchWithCredentials<StopImpersonationResponse>(endpoints.impersonationStop, {
    method: 'POST',
  });
}

/**
 * Export users as CSV (admin only)
 * Returns a download URL or blob
 */
export async function exportUsers(): Promise<Blob> {
  const response = await fetch(`${BASE_URL}${endpoints.exportUsers}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new UserLoginApiError('Export failed', response.status);
  }
  
  return response.blob();
}

/**
 * Activate a user (admin only)
 */
export async function activateUser(
  customerId: number,
  approveWithoutDemo?: boolean
): Promise<ActivateUserResponse> {
  const body: ActivateUserRequest = {
    customerID: customerId,
    approveWithoutDemo,
  };
  
  return fetchWithCredentials<ActivateUserResponse>(endpoints.activateUser, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Deactivate a user (admin only)
 */
export async function deactivateUser(customerId: number): Promise<DeactivateUserResponse> {
  const body: DeactivateUserRequest = {
    customerID: customerId,
  };
  
  return fetchWithCredentials<DeactivateUserResponse>(endpoints.deactivateUser, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ============================================
// Utility
// ============================================

/**
 * Check if a user is anonymous
 */
export async function isUserAnonymous(userId: number): Promise<IsUserAnonymousResponse> {
  return fetchWithCredentials<IsUserAnonymousResponse>(endpoints.isUserAnonymous, {
    method: 'POST',
    body: JSON.stringify({ userID: userId }),
  });
}

/**
 * Get anonymous cart ID
 */
export async function getCart(): Promise<GetCartResponse> {
  const response = await fetch(`${BASE_URL}${endpoints.getCart}`, {
    credentials: 'include',
  });
  
  if (!response.ok) {
    throw new UserLoginApiError('Failed to get cart', response.status);
  }
  
  return response.json();
}

// ============================================
// Feedback
// ============================================

export interface FeedbackPayload {
  category: string;
  message: string;
  page?: string;
  posthog_replay_url?: string;
}

export interface FeedbackResponse {
  id: number;
  user_id: number | null;
  category: string;
  message: string;
  page: string | null;
  status: string;
  admin_notes: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export async function submitFeedback(data: FeedbackPayload): Promise<FeedbackResponse> {
  return fetchWithCredentials<FeedbackResponse>(endpoints.submitFeedback, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================
// Analytics
// ============================================

/**
 * Get user login analytics
 */
export async function getUserLoginAnalytics(
  userId: number
): Promise<UserLoginAnalyticsResponse> {
  const response = await fetch(
    `${BASE_URL}${endpoints.userLoginAnalytics}?user_id=${userId}`,
    {
      credentials: 'include',
    }
  );
  
  if (!response.ok) {
    throw new UserLoginApiError('Failed to get analytics', response.status);
  }
  
  return response.json();
}

// ============================================
// API Class
// ============================================

/**
 * User Login API class for advanced usage
 */
export class UserLoginAPI {
  // Authentication
  login = login;
  logout = logout;
  validateSession = validateSession;
  
  // Registration
  register = register;
  activateAccount = activateAccount;
  resendActivationLink = resendActivationLink;
  
  // User Profile
  getUserDetails = getUserDetails;
  updateUserDetails = updateUserDetails;
  getUserStorageSpace = getUserStorageSpace;
  updateJourney = updateJourney;
  getOnboardingPreference = getOnboardingPreference;
  updateOnboardingPreference = updateOnboardingPreference;
  
  // Password Management
  updatePassword = updatePassword;
  forgotPassword = forgotPassword;
  validateResetToken = validateResetToken;
  resetPassword = resetPassword;
  
  // Address Management
  getAddresses = getAddresses;
  addAddress = addAddress;
  editAddress = editAddress;
  deleteAddress = deleteAddress;
  
  // Administration
  checkAdmin = checkAdmin;
  listAdmins = listAdmins;
  addAdmin = addAdmin;
  removeAdmin = removeAdmin;
  listUsers = listUsers;
  getImpersonationSession = getImpersonationSession;
  listImpersonationUsers = listImpersonationUsers;
  startImpersonation = startImpersonation;
  stopImpersonation = stopImpersonation;
  exportUsers = exportUsers;
  activateUser = activateUser;
  deactivateUser = deactivateUser;
  
  // Utility
  isUserAnonymous = isUserAnonymous;
  getCart = getCart;

  // Feedback
  submitFeedback = submitFeedback;

  // Analytics
  getUserLoginAnalytics = getUserLoginAnalytics;
}

export const userLoginApi = new UserLoginAPI();
export default userLoginApi;








